/* eslint-disable @typescript-eslint/unbound-method,consistent-return */
import { Instrument as InstrumentBase } from '../../bases/instrument';
import * as CONSTANTS from './constants';
import { globalScope } from '../../window/browser';
import { getConfigurationValue } from '../../state/init';
import { Aggregator } from './aggregator';
import { MainQueue } from '../queue';
import { isSampled } from '../../utils/sampling';

export class Instrument extends InstrumentBase {
  static featureName = CONSTANTS.SIGNAL_NAME;

  constructor() {
    const aggregator = new Aggregator();

    super(aggregator, Instrument.featureName);
    this.queue = new MainQueue();

    this.start = this.start.bind(this);
    this.recordEvent = this.recordEvent.bind(this);
    this.recordLog = this.recordLog.bind(this);
    this.info = this.info.bind(this);
    this.warn = this.warn.bind(this);
    this.error = this.error.bind(this);
    this.fatal = this.fatal.bind(this);
    this.attachLogExporter = this.attachLogExporter.bind(this);
    this.config = getConfigurationValue(
      `instruments.signals.${Instrument.featureName}`,
    );

    if (!this.hasKey) {
      return;
    }

    if (this.auto) {
      this.start();
    }

    if (this.loadFunctions) {
      this.loadPublicFunctions();
    }
  }

  loadPublicFunctions() {
    if (globalScope.WMANS) {
      globalScope.WMANS.logs = {};
      globalScope.WMANS.logs.attachLogExporter = this.attachLogExporter;
      globalScope.WMANS.logs.start = this.start;
      globalScope.WMANS.logs.info = this.info;
      globalScope.WMANS.logs.warn = this.warn;
      globalScope.WMANS.logs.error = this.error;
      globalScope.WMANS.logs.fatal = this.fatal;
      globalScope.WMANS.recordEvent = this.event;
      globalScope.WMANS.recordLog = this.info;
    }
  }

  start() {
    void import('./module').then(({ Module: Logs }) => {
      this.module = new Logs(
        this.aggregator,
        this.config,
        getConfigurationValue('otel'),
      );
      this.processQueue();
    });
  }

  recordEvent(event, exporterConfig = 'default', internal = false) {
    /* istanbul ignore next */
    if (!isSampled(this.config.sampling, internal)) {
      return;
    }

    if (!this.module) {
      this.queue.push(Instrument.featureName, {
        type: 'recordEvent',
        attributes: {
          event,
          exporterConfig,
        },
      });

      return;
    }

    return this.module.recordEvent(event, exporterConfig);
  }

  recordLog(event, exporterConfig = 'default', internal = false) {
    /* istanbul ignore next */
    if (!isSampled(this.config.sampling, internal)) {
      return;
    }

    if (!this.module) {
      this.queue.push(Instrument.featureName, {
        type: 'recordLog',
        attributes: {
          event,
          exporterConfig,
        },
      });

      return;
    }

    return this.module.recordLog(event, exporterConfig);
  }

  info(message, attributes = {}, internal = false) {
    /* istanbul ignore next */
    if (!isSampled(this.config.sampling, internal)) {
      return;
    }

    return this.recordLog({
      attributes: {
        level: 'INFO',
        ...attributes,
      },
      body: message,
      severityNumber: 9,
      severityText: 'INFO',
    });
  }

  warn(message, attributes = {}, internal = false) {
    /* istanbul ignore next */
    if (!isSampled(this.config.sampling, internal)) {
      return;
    }

    return this.recordLog({
      attributes: {
        level: 'WARN',
        ...attributes,
      },
      body: message,
      severityNumber: 13,
      severityText: 'WARN',
    });
  }

  error(message, attributes = {}, internal = false) {
    /* istanbul ignore next */
    if (!isSampled(this.config.sampling, internal)) {
      return;
    }

    return this.recordLog({
      attributes: {
        level: 'ERROR',
        ...attributes,
      },
      body: message,
      severityNumber: 17,
      severityText: 'ERROR',
    });
  }

  fatal(message, attributes = {}, internal = false) {
    /* istanbul ignore next */
    if (!isSampled(this.config.sampling, internal)) {
      return;
    }

    if (!internal) {
      return;
    }

    return this.recordLog({
      attributes: {
        level: 'FATAL',
        ...attributes,
      },
      body: message,
      severityNumber: 19,
      severityText: 'FATAL',
    });
  }

  attachLogExporter(name, config) {
    if (!this.module) {
      return;
    }

    this.module.attachLogExporter(name, config);
  }

  processQueue() {
    const payloads = this.queue.pop(Instrument.featureName);

    if (!payloads) {
      return;
    }

    payloads.forEach((payload) => {
      switch (payload.type) {
        case 'recordEvent':
          this.recordEvent(
            payload.attributes.event,
            payload.attributes.exporterConfig,
          );

          break;
        case 'recordLog':
          this.recordLog(
            payload.attributes.event,
            payload.attributes.exporterConfig,
          );

          break;

        default:
          break;
      }
    });
  }
}
